import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { t } from '../lib/i18n';
import { friendlyError, friendlyText } from '../lib/friendly-error';

export type ToastKind = 'info' | 'ok' | 'warn' | 'error';
interface ToastOptions {
  /** Raw message: click "详情" (Details) to expand (stays open once expanded) */
  detail?: string;
  /** A new toast in the same group replaces the old one (e.g. login failures: keep only the latest reason) */
  group?: string;
}
interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
  detail: string | null;
  group: string | null;
  open: boolean;
  leaving: boolean;
}

const EVENT = 'momomail:toast';
let seq = 0;

/** Pop up a line at the bottom of the screen: deeply nested components don't need callbacks passed down through every layer. */
export function toast(text: string, kind: ToastKind = 'info', options: ToastOptions = {}) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { text, kind, detail: options.detail ?? null, group: options.group ?? null } }));
}

/** Something went wrong: explain in plain words by error kind (can't connect / certificate / wrong password…), with the raw message under "详情" (Details). */
export function toastError(error: unknown, group?: string) {
  const e = friendlyError(error);
  toast(friendlyText(error), e.severity, { ...(e.technical && e.detail ? { detail: e.detail } : {}), ...(group ? { group } : {}) });
}

export function Toasts() {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, number[]>());
  const close = (id: number) => {
    for (const timer of timers.current.get(id) ?? []) clearTimeout(timer);
    timers.current.delete(id);
    setItems((list) => list.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    setTimeout(() => setItems((list) => list.filter((x) => x.id !== id)), 200);
  };
  useEffect(() => {
    const onToast = (e: Event) => {
      const { text, kind, detail, group } = (e as CustomEvent<{ text: string; kind: ToastKind; detail: string | null; group: string | null }>).detail;
      const id = ++seq;
      setItems((list) => [...list.filter((x) => x.text !== text && (!group || x.group !== group)).slice(-3), { id, text, kind, detail, group, open: false, leaving: false }]);
      const ttl = kind === 'error' || kind === 'warn' ? 8000 : 3200;
      timers.current.set(id, [window.setTimeout(() => close(id), ttl)]);
    };
    window.addEventListener(EVENT, onToast);
    return () => window.removeEventListener(EVENT, onToast);
  }, []);
  const toggle = (id: number) => {
    // Don't auto-dismiss once details are expanded
    for (const timer of timers.current.get(id) ?? []) clearTimeout(timer);
    timers.current.delete(id);
    setItems((list) => list.map((x) => (x.id === id ? { ...x, open: !x.open } : x)));
  };
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`toast toast--${item.kind} ${item.leaving ? 'toast--leaving' : ''}`}>
          <div className="toast__row">
            <Icon name={item.kind === 'error' || item.kind === 'warn' ? 'alert' : item.kind === 'ok' ? 'check' : 'mail'} size={16} />
            <span className="toast__text">{item.text}</span>
            {item.detail && (
              <button className="bare toast__more" aria-expanded={item.open} onClick={() => toggle(item.id)}>
                {t('详情')}
              </button>
            )}
            <button className="bare toast__x" aria-label={t('关掉')} onClick={() => close(item.id)}>
              <Icon name="x" size={14} />
            </button>
          </div>
          {item.open && item.detail && <pre className="toast__detail">{item.detail}</pre>}
        </div>
      ))}
    </div>
  );
}
